<template>
  <div class="messages-area" :style="themeVars">
    <div
      class="messages-area__shell"
      :class="{ 'messages-area__shell--mobile': isMobile }"
    >
      <section
        v-show="!isMobile || !selected"
        class="messages-area__list"
        :aria-label="$t('messages.conversations')"
      >
        <conversation-list
          :items="visibleItems"
          :active-key="selectedKey"
          :tab.sync="tab"
          :search.sync="search"
          :unread="unread"
          :loading="loadingList"
          :whatsapp-link="whatsappLink"
          :is-teacher="isTeacher"
          @select="select"
        />
      </section>

      <section
        v-show="!isMobile || selected"
        class="messages-area__chat"
        :aria-label="$t('messages.openConversation')"
      >
        <chat-panel
          :conversation="selected"
          :messages="messages"
          :has-more="hasMore"
          :loading="loadingMessages"
          :loading-more="loadingMore"
          :sending="sending"
          :show-back="isMobile"
          :whatsapp-link="whatsappLink"
          :accent="accent"
          @back="closeConversation"
          @send="send"
          @load-more="loadMore"
          @image-error="refreshImages"
        />
      </section>
    </div>

    <v-snackbar v-model="snackbar.show" :timeout="5000" :color="snackbar.color">
      {{ snackbar.text }}
    </v-snackbar>
  </div>
</template>

<script>
import ConversationList from "./ConversationList.vue";
import ChatPanel from "./ChatPanel.vue";
import {
  conversationKey,
  cursorGreater,
  sameConversation,
} from "@/shared/messagesKeys";

// Card 17trt6jbffe: tela Mensagens, compartilhada por aluno e professor. A
// unica diferenca e o cliente da API (prop `api`), que ja aponta para
// /customers ou /partners.
//
// Atualizacao por polling (o servidor atual nao suporta WebSocket): conversa
// aberta a cada 5 s pedindo so o que veio depois do ultimo id; lista a cada
// 30 s. Tudo pausa com a aba do navegador oculta.

const CHAT_POLL_MS = 5000;
const LIST_POLL_MS = 30000;

function fold(value) {
  return String(value || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .trim();
}

export default {
  name: "MessagesArea",
  components: { ConversationList, ChatPanel },
  props: {
    api: { type: Object, required: true },
    profile: { type: String, default: "customer" },
  },
  data() {
    return {
      items: [],
      unread: { total: 0, lessons: 0, support: 0 },
      tab: "lessons",
      search: "",
      loadingList: true,
      selected: null,
      messages: [],
      hasMore: false,
      loadingMessages: false,
      loadingMore: false,
      sending: false,
      settings: { whatsappNumber: null, displayName: "Suporte Pitch" },
      chatTimer: null,
      listTimer: null,
      polling: false,
      lastImageRefresh: 0,
      snackbar: { show: false, text: "", color: "error" },
    };
  },
  computed: {
    isTeacher() {
      return this.profile === "partner";
    },
    isMobile() {
      return this.$vuetify.breakpoint.smAndDown;
    },
    accent() {
      return this.isTeacher ? "#a826e1" : "#e8276f";
    },
    themeVars() {
      return this.isTeacher
        ? {
            "--msg-accent": "#a826e1",
            "--msg-accent-soft": "rgba(168, 38, 225, 0.1)",
            "--msg-bubble-mine": "#f1e2fa",
          }
        : {
            "--msg-accent": "#e8276f",
            "--msg-accent-soft": "rgba(232, 39, 111, 0.1)",
            "--msg-bubble-mine": "#fde4ee",
          };
    },
    selectedKey() {
      return this.selected ? conversationKey(this.selected) : null;
    },
    // Busca local (a lista ja vem inteira): nome do contato, codigo da turma e
    // nomes dos membros, sem acento e sem caixa.
    visibleItems() {
      const tokens = fold(this.search).split(/\s+/).filter(Boolean);
      return this.items.filter((item) => {
        if (item.tab !== this.tab) return false;
        if (!tokens.length) return true;
        const haystack = fold(
          [
            item.title,
            item.subtitle,
            ...(item.members || []).map((member) => member.name),
          ].join(" ")
        );
        return tokens.every((token) => haystack.includes(token));
      });
    },
    // Sem dados pessoais no link: so o numero da Pitch e o texto fixo.
    whatsappLink() {
      const digits = String(this.settings.whatsappNumber || "").replace(
        /\D/g,
        ""
      );
      if (!digits) return null;
      return `https://wa.me/${digits}?text=${encodeURIComponent(
        this.$t("messages.whatsappText")
      )}`;
    },
    lastMessageId() {
      const last = this.messages[this.messages.length - 1];
      return last ? String(last.id) : null;
    },
  },
  async mounted() {
    document.addEventListener("visibilitychange", this.onVisibility);
    this.loadSettings();
    await this.loadList();
    this.startTimers();
  },
  beforeDestroy() {
    document.removeEventListener("visibilitychange", this.onVisibility);
    this.stopTimers();
  },
  methods: {
    startTimers() {
      this.stopTimers();
      this.chatTimer = window.setInterval(this.pollConversation, CHAT_POLL_MS);
      this.listTimer = window.setInterval(
        () => this.loadList({ silent: true }),
        LIST_POLL_MS
      );
    },
    stopTimers() {
      if (this.chatTimer) window.clearInterval(this.chatTimer);
      if (this.listTimer) window.clearInterval(this.listTimer);
      this.chatTimer = null;
      this.listTimer = null;
    },
    onVisibility() {
      if (document.visibilityState === "hidden") {
        this.stopTimers();
        return;
      }
      this.loadList({ silent: true });
      this.pollConversation();
      this.startTimers();
    },
    notify(text, color = "error") {
      this.snackbar = { show: true, text, color };
    },
    async loadSettings() {
      try {
        const { data } = await this.api.getMessagesSupportSettings();
        this.settings = {
          whatsappNumber: data?.whatsappNumber || null,
          displayName: data?.displayName || "Suporte Pitch",
        };
      } catch {
        // Sem configuracao o botao do WhatsApp so nao aparece.
      }
    },
    async loadList({ silent = false } = {}) {
      if (!silent) this.loadingList = true;
      try {
        const { data } = await this.api.listMessageConversations();
        this.items = data?.data || [];
        this.unread = data?.unread || { total: 0, lessons: 0, support: 0 };
        this.$store.commit("SET_MESSAGES_UNREAD", this.unread);

        if (this.selected) {
          const fresh = this.items.find((item) =>
            sameConversation(item, this.selected)
          );
          if (fresh) {
            // A conversa aberta esta lida para quem esta olhando.
            this.selected = { ...fresh, unread: 0 };
          } else if (this.selected.id) {
            this.conversationGone();
          }
        }
      } catch (error) {
        if (!silent) this.notify(this.$t("messages.listFailed"));
      } finally {
        this.loadingList = false;
      }
    },
    async select(item) {
      if (this.selected && sameConversation(item, this.selected)) return;
      this.selected = { ...item };
      this.messages = [];
      this.hasMore = false;

      let current = item;
      if (!item.id) {
        try {
          const { data } = await this.api.openMessageConversation({
            type: item.key.type,
            partnerId: item.key.partnerId || undefined,
            customerId: item.key.customerId || undefined,
            classId: item.key.classId || undefined,
          });
          current = data?.data;
          this.replaceItem(item, current);
          this.selected = { ...current };
        } catch (error) {
          this.handleError(error);
          return;
        }
      }

      await this.loadInitial(current);
    },
    replaceItem(previous, next) {
      const index = this.items.findIndex((item) =>
        sameConversation(item, previous)
      );
      if (index >= 0)
        this.items.splice(index, 1, { ...this.items[index], ...next });
    },
    async loadInitial(item, { silent = false } = {}) {
      if (!item?.id) return;
      if (!silent) this.loadingMessages = true;
      try {
        const { data } = await this.api.listConversationMessages(item.id);
        if (!this.selected || String(this.selected.id) !== String(item.id)) {
          return;
        }
        this.messages = data?.data || [];
        this.hasMore = !!data?.hasMore;
        await this.markRead();
      } catch (error) {
        this.handleError(error);
      } finally {
        this.loadingMessages = false;
      }
    },
    async loadMore() {
      if (!this.selected?.id || !this.messages.length) return;
      this.loadingMore = true;
      try {
        const { data } = await this.api.listConversationMessages(
          this.selected.id,
          { before: String(this.messages[0].id) }
        );
        const older = data?.data || [];
        const known = new Set(this.messages.map((m) => String(m.id)));
        this.messages = [
          ...older.filter((m) => !known.has(String(m.id))),
          ...this.messages,
        ];
        this.hasMore = !!data?.hasMore;
      } catch (error) {
        this.handleError(error);
      } finally {
        this.loadingMore = false;
      }
    },
    async pollConversation() {
      if (
        this.polling ||
        !this.selected?.id ||
        this.loadingMessages ||
        document.visibilityState === "hidden"
      ) {
        return;
      }
      this.polling = true;
      const conversationId = this.selected.id;
      try {
        const params = this.lastMessageId ? { after: this.lastMessageId } : {};
        const { data } = await this.api.listConversationMessages(
          conversationId,
          params
        );
        if (String(this.selected?.id) !== String(conversationId)) return;
        const incoming = data?.data || [];
        if (!this.lastMessageId) {
          this.messages = incoming;
          this.hasMore = !!data?.hasMore;
        } else {
          this.appendMessages(incoming);
        }
        if (incoming.some((message) => !message.mine)) await this.markRead();
      } catch (error) {
        this.handleError(error, { silent: true });
      } finally {
        this.polling = false;
      }
    },
    appendMessages(incoming) {
      const known = new Set(this.messages.map((m) => String(m.id)));
      const fresh = incoming.filter((m) => !known.has(String(m.id)));
      if (!fresh.length) return;
      this.messages = [...this.messages, ...fresh].sort((a, b) =>
        cursorGreater(a.id, b.id) ? 1 : -1
      );
      const last = this.messages[this.messages.length - 1];
      this.patchSelectedItem({
        lastMessage: {
          id: last.id,
          preview: last.body || (last.file ? this.$t("messages.image") : ""),
          hasImage: !!last.file,
          mine: !!last.mine,
          authorName: null,
          createdAt: last.createdAt,
        },
        lastMessageAt: last.createdAt,
      });
    },
    patchSelectedItem(patch) {
      if (!this.selected) return;
      const index = this.items.findIndex((item) =>
        sameConversation(item, this.selected)
      );
      if (index >= 0) {
        this.items.splice(index, 1, { ...this.items[index], ...patch });
      }
    },
    async markRead() {
      if (!this.selected?.id || !this.lastMessageId) return;
      const index = this.items.findIndex((item) =>
        sameConversation(item, this.selected)
      );
      const wasUnread = index >= 0 ? Number(this.items[index].unread) || 0 : 0;
      try {
        await this.api.markConversationRead(
          this.selected.id,
          this.lastMessageId
        );
      } catch {
        return;
      }
      if (wasUnread) {
        this.patchSelectedItem({ unread: 0 });
        const tab = this.selected.tab === "support" ? "support" : "lessons";
        const next = { ...this.unread };
        next[tab] = Math.max(0, (Number(next[tab]) || 0) - wasUnread);
        next.total = (Number(next.lessons) || 0) + (Number(next.support) || 0);
        this.unread = next;
        this.$store.commit("SET_MESSAGES_UNREAD", next);
      }
    },
    async send(payload, { resolve, reject }) {
      if (!this.selected?.id) {
        reject(new Error(this.$t("messages.notSelected")));
        return;
      }
      this.sending = true;
      try {
        const { data } = await this.api.sendConversationMessage(
          this.selected.id,
          payload
        );
        if (data?.data) this.appendMessages([data.data]);
        resolve();
      } catch (error) {
        if (error?.response?.status === 404) {
          this.conversationGone();
          resolve();
        } else {
          reject(error);
        }
      } finally {
        this.sending = false;
      }
    },
    // As URLs das imagens valem 15 minutos: se expirou, recarrega a pagina
    // atual da conversa (no maximo a cada 30 s).
    refreshImages() {
      if (Date.now() - this.lastImageRefresh < 30000 || !this.selected) return;
      this.lastImageRefresh = Date.now();
      this.loadInitial(this.selected, { silent: true });
    },
    handleError(error, { silent = false } = {}) {
      if (error?.response?.status === 404) {
        this.conversationGone();
        return;
      }
      if (!silent) {
        this.notify(
          error?.response?.data?.message || this.$t("messages.loadFailed")
        );
      }
    },
    // A conversa deixou de estar disponivel (saiu da turma, trocou de
    // professor...): volta para a lista com um aviso.
    conversationGone() {
      this.notify(this.$t("messages.gone"));
      this.closeConversation();
      this.loadList({ silent: true });
    },
    closeConversation() {
      this.selected = null;
      this.messages = [];
      this.hasMore = false;
    },
  },
};
</script>

<style scoped>
.messages-area {
  padding: 8px 0 16px;
}
.messages-area__shell {
  display: flex;
  height: calc(100vh - 120px);
  min-height: 480px;
  border-radius: 16px;
  overflow: hidden;
  background: var(--dk-surface, #fff);
  border: 1px solid rgba(var(--dk-ink-rgb, 35, 26, 46), 0.08);
  box-shadow: 0 6px 24px rgba(var(--dk-shadow-rgb, 35, 26, 46), 0.06);
}
.messages-area__list {
  flex: 0 0 360px;
  max-width: 360px;
  border-right: 1px solid rgba(var(--dk-ink-rgb, 35, 26, 46), 0.08);
  min-height: 0;
}
.messages-area__chat {
  flex: 1 1 auto;
  min-width: 0;
  min-height: 0;
}
.messages-area__shell--mobile {
  height: calc(100vh - 88px);
  border-radius: 12px;
  margin-top: 48px;
}
.messages-area__shell--mobile .messages-area__list {
  flex: 1 1 auto;
  max-width: none;
  border-right: none;
}
</style>
